import type { AccountingHeadingNode } from '@/lib/api/accounting';

export type AccountHierarchyLevel = {
  code: string;
  title: string;
};

export type AccountHierarchy = {
  general: AccountHierarchyLevel | null;
  subsidiary: AccountHierarchyLevel | null;
  detail: AccountHierarchyLevel | null;
};

export function buildAccountTitleByCode(tree: AccountingHeadingNode[]): Record<string, string> {
  const map: Record<string, string> = {};
  const walk = (items: AccountingHeadingNode[]) => {
    items.forEach((node) => {
      if (node.code) map[node.code] = node.title || '';
      if (node.children?.length) walk(node.children);
    });
  };
  walk(tree);
  return map;
}

function levelFromCode(code: string): 'general' | 'subsidiary' | 'detail' {
  if (code.length <= 2) return 'general';
  if (code.length <= 4) return 'subsidiary';
  return 'detail';
}

function pickLevel(
  code: string,
  titleByCode: Record<string, string>,
  fallbackTitle?: string,
): AccountHierarchyLevel {
  const title = titleByCode[code] || fallbackTitle || '';
  return { code, title };
}

/** از کد حساب ردیف سند، سلسله‌مراتب کل / معین / تفصیلی را استخراج می‌کند */
export function resolveAccountHierarchy(
  accountCode: string,
  titleByCode: Record<string, string>,
  lineAccountTitle?: string,
): AccountHierarchy {
  const code = (accountCode || '').replace(/\D/g, '');
  if (!code) {
    return { general: null, subsidiary: null, detail: null };
  }

  const leaf = levelFromCode(code);
  const fallback = (lineAccountTitle || '').trim();

  const generalCode = code.slice(0, 2);
  const subsidiaryCode = code.length >= 4 ? code.slice(0, 4) : '';
  const detailCode = code.length > 4 ? code : '';

  const general =
    code.length >= 2
      ? pickLevel(generalCode, titleByCode, leaf === 'general' ? fallback : undefined)
      : null;

  const subsidiary =
    code.length >= 4
      ? pickLevel(subsidiaryCode, titleByCode, leaf === 'subsidiary' ? fallback : undefined)
      : null;

  const detail =
    detailCode
      ? pickLevel(detailCode, titleByCode, leaf === 'detail' ? fallback : undefined)
      : null;

  return { general, subsidiary, detail };
}

export const ACCOUNT_LEVEL_LABELS = [
  { key: 'general' as const, label: 'کل' },
  { key: 'subsidiary' as const, label: 'معین' },
  { key: 'detail' as const, label: 'تفصیلی' },
];
